import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield, FileText, Mail, Cookie } from 'lucide-react';

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-6 text-slate-600"
          data-testid="privacy-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
        </Button>

        <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-slate-900">Informativa Privacy</h1>
          </div>
          <p className="text-sm text-slate-500 mb-8">
            Ai sensi del Regolamento UE 2016/679 (GDPR) e del D.Lgs. 196/2003
          </p>

          <section className="space-y-6 text-slate-700 text-sm leading-relaxed">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" /> 1. Titolare del trattamento
              </h2>
              <p>
                <strong>EDILSTUDIO s.r.l.</strong> — Email:{' '}
                <a href="mailto:info@semplificaffitto.it" className="text-blue-600 underline">
                  info@semplificaffitto.it
                </a>
              </p>
              <p className="mt-1">
                Il Titolare è responsabile del trattamento dei dati personali raccolti tramite la
                piattaforma <strong>Semplificaffitto.it</strong> ("la Piattaforma").
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">2. Dati raccolti</h2>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Dati anagrafici</strong>: nome, cognome, codice fiscale, data di nascita, residenza</li>
                <li><strong>Dati di contatto</strong>: email, telefono</li>
                <li><strong>Dati bancari</strong>: IBAN, dati fiscali (Partita IVA)</li>
                <li><strong>Dati contrattuali</strong>: contratti di locazione, canoni, pagamenti</li>
                <li><strong>Dati documentali</strong>: documenti caricati dall'amministratore (carta d'identità, fatture, ecc.)</li>
                <li><strong>Dati tecnici di accesso</strong>: indirizzo IP, log di accesso, tentativi di login (per sicurezza)</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">3. Finalità e base giuridica</h2>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Esecuzione del contratto</strong> (Art. 6.1.b GDPR): gestione affitti, pagamenti, fatturazione</li>
                <li><strong>Obblighi di legge</strong> (Art. 6.1.c): adempimenti fiscali, registrazione contratti all'Agenzia delle Entrate, antiriciclaggio</li>
                <li><strong>Legittimo interesse</strong> (Art. 6.1.f): sicurezza dei sistemi informatici, prevenzione frodi, audit trail</li>
                <li><strong>Consenso</strong> (Art. 6.1.a): solo per comunicazioni commerciali facoltative (non utilizzato attualmente)</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">4. Conservazione dei dati</h2>
              <p>I dati personali vengono conservati per il tempo strettamente necessario:</p>
              <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
                <li><strong>Documenti fiscali e contabili</strong>: 10 anni (art. 2220 c.c.)</li>
                <li><strong>Contratti di locazione registrati</strong>: 10 anni dal termine</li>
                <li><strong>Log di accesso/audit</strong>: 24 mesi</li>
                <li><strong>Dati di profilo (utenti non attivi)</strong>: fino a 12 mesi dalla cessazione, salvo richiesta di cancellazione</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">5. I tuoi diritti (Artt. 15-22 GDPR)</h2>
              <p>In qualsiasi momento puoi esercitare i seguenti diritti:</p>
              <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
                <li><strong>Accesso</strong> (Art. 15): conoscere quali dati abbiamo su di te</li>
                <li><strong>Rettifica</strong> (Art. 16): correggere dati inesatti</li>
                <li><strong>Cancellazione</strong> (Art. 17): chiedere la cancellazione dei tuoi dati (entro i limiti di legge)</li>
                <li><strong>Limitazione</strong> (Art. 18): bloccare temporaneamente il trattamento</li>
                <li><strong>Portabilità</strong> (Art. 20): ricevere i tuoi dati in formato JSON strutturato</li>
                <li><strong>Opposizione</strong> (Art. 21): opporti al trattamento per legittimo interesse</li>
              </ul>
              <p className="mt-2">
                <strong>Come esercitarli</strong>: scrivi a{' '}
                <a href="mailto:info@semplificaffitto.it?subject=Richiesta%20GDPR%20%E2%80%94%20Esercizio%20diritti%20Art.%2015-22" className="text-blue-600 underline">
                  info@semplificaffitto.it
                </a>
                {' '}specificando il diritto che intendi esercitare. Ti risponderemo entro 30 giorni.
                Nota: per i dati soggetti ad obbligo di conservazione decennale procederemo,
                dove possibile, con l'anonimizzazione mantenendo solo le informazioni necessarie
                agli adempimenti di legge.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Cookie className="w-5 h-5 text-amber-600" /> 6. Cookie utilizzati
              </h2>
              <p>
                La Piattaforma utilizza <strong>esclusivamente cookie tecnici essenziali</strong>{' '}
                necessari al funzionamento del servizio (autenticazione, sessione utente). Non
                utilizziamo cookie di profilazione né di terze parti. Per questo non è richiesto
                il consenso al loro utilizzo (Art. 122 D.Lgs. 196/2003).
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2 mt-2">
                <li>
                  <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">sa_auth</code>:
                  cookie di sessione, httpOnly+Secure+SameSite=Lax. Dura 24 ore. Non condiviso
                  con terze parti.
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">7. Sicurezza dei dati</h2>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Connessione HTTPS obbligatoria (TLS 1.2+)</li>
                <li>Password salvate con hashing bcrypt</li>
                <li>Autenticazione a due fattori (2FA TOTP) obbligatoria per gli amministratori</li>
                <li>Rate limiting + account lockout dopo 3 tentativi falliti</li>
                <li>Cookie di sessione protetti (httpOnly, inaccessibili a JavaScript)</li>
                <li>Mascheramento PII nei log di sistema</li>
                <li>Storage documenti su Object Storage cifrato (Aruba S3, datacenter Italia)</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">8. Trasferimento dei dati</h2>
              <p>
                I dati sono ospitati esclusivamente su server localizzati nello{' '}
                <strong>Spazio Economico Europeo (Italia)</strong>. Non avviene trasferimento di dati
                personali al di fuori dell'UE.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">9. Reclami</h2>
              <p>
                Se ritieni che il trattamento dei tuoi dati personali violi il GDPR, hai diritto di
                proporre reclamo al <strong>Garante per la Protezione dei Dati Personali</strong>:{' '}
                <a
                  href="https://www.garanteprivacy.it"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  www.garanteprivacy.it
                </a>
              </p>
            </div>

            <div className="border-t pt-4 mt-6 text-xs text-slate-500">
              <p>
                <Mail className="w-3 h-3 inline mr-1" />
                Per qualsiasi domanda sulla privacy:{' '}
                <a href="mailto:info@semplificaffitto.it" className="text-blue-600 underline">
                  info@semplificaffitto.it
                </a>
              </p>
              <p className="mt-1">Ultimo aggiornamento: 29/05/2026 — Versione 1.0</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
